import { effect, inject, untracked } from '@angular/core';
import { patchState, signalStore, signalStoreFeature, withHooks } from '@ngrx/signals';
import { AuthenticationService } from 'src/shared/services/auth/auth_service';
import { withCruftPackages } from './cruft-packages-feature';
import { withEntitlementLookupCache } from './entitlement-lookup-cache.feature';
import { withEntitlementLookup } from './entitlement-lookup.feature';
import { withUserEntitlements } from './user-entitlements.feature';

/** Default state used when the store is reset (e.g. on logout). */
const initialState = {
  userEntitlementsState: 'initial' as const,
  userEntitlementsError: '',
  userEntitlements: null as null,
  lookupState: 'idle' as const,
  lookupError: '',
  applyMembershipState: 'idle' as const,
  applyMembershipError: '',
  applyMembershipResult: null as null,
  lastLookupResult: null as null,
  cruftPackagesState: 'initial' as const,
  cruftPackagesError: '',
  starterPackage: null as null,
  plusPackage: null as null,
  ultimatePackage: null as null,
};

/** Resets the store to initial state when the user logs out. */
function withEntitlementsAuthReset() {
  return signalStoreFeature(
    withHooks({
      onInit(store, authService = inject(AuthenticationService)) {
        effect(() => {
          const isLoggedIn = authService.loggedIn();
          untracked(() => {
            if (!isLoggedIn) {
              patchState(store, initialState);
            }
          });
        });
      },
    })
  );
}

/**
 * Root entitlements store. Composes user entitlements, lookup (with cache), cruft packages,
 * and auth reset. Injected as a singleton; use for entitlement state and lookup/apply actions.
 */
export const EntitlementsStore = signalStore(
  { providedIn: 'root' },
  withUserEntitlements(),
  withEntitlementLookupCache(),
  withEntitlementLookup(),
  withCruftPackages(),
  withEntitlementsAuthReset()
);
